// src/pages/gt3x/standings/StandingsTeam.jsx
import { useEffect, useMemo, useState } from "react";
import { fetchSheetViaProxy } from "../../lib/sheets";

const RANGE = "standings_team!A1:AI100";

// "A" -> 1, "Z" -> 26, "AA" -> 27, ...
const colIndex = (letter) => {
    let n = 0;
    for (let i = 0; i < letter.length; i++) n = n * 26 + (letter.charCodeAt(i) - 64);
    return n;
};

const LETTER_AA = colIndex("AA"); // 27 (letzte sichtbare Spalte)
//const LETTER_AB = colIndex("AB"); // 28 (Teamlogo-URL, unsichtbar)
const LETTER_M  = colIndex("M");  // 13 (R1)
const LETTER_X  = colIndex("X");  // 24 (R12)
const LETTER_Y  = colIndex("Y");  // 25 (Drop1)
//const LETTER_AZ = colIndex("AZ"); // 52 (falls gebraucht, wir begrenzen auf AA visuell)

// Hilfsfunktionen
const isNumeric = (v) => v !== null && v !== "" && !isNaN(Number(v));

function computeTop3ByColumn(rowsAsArrays, startCol, endCol) {
    const map = new Map();
    for (let c = startCol; c <= endCol; c++) {
        const nums = rowsAsArrays
            .map(arr => arr[c - 1])
            .filter(v => v !== null && v !== undefined && v !== "")
            .map(v => Number(v))
            .filter(v => !Number.isNaN(v));
        if (!nums.length) { map.set(c, null); continue; }

        const uniqDesc = Array.from(new Set(nums)).sort((a, b) => b - a);
        map.set(c, {
            gold:   uniqDesc[0] ?? null,
            silver: uniqDesc[1] ?? null,
            bronze: uniqDesc[2] ?? null,
        });
    }
    return map;
}

export default function StandingsTeam() {
    const [data, setData] = useState({ header: [], rows: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetchSheetViaProxy({ range: RANGE });
                setData(res);
            } catch (e) {
                const msg = e?.message || String(e);
                // Sonderfall: Range existiert noch nicht -> freundliche Empty-Message
                if (msg.includes("Unable to parse range")) {
                    setNoData(true);
                } else {
                    setError(msg);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const { visibleHeaders, idxTeamLogoUrl, rowsAsArrays, top3PerRaceColumn, completedRuns } = useMemo(() => {
        const headers = data.header || [];

        // Sichtbar A..AA (27 Spalten)
        const visibleHeaders = headers.slice(0, LETTER_AA);

        // Teamlogo-URL liegt in AB
        const idxTeamLogoUrl = colIndex("AB") - 1;

        // rowsAsArrays in Index-Form
        const rowsAsArrays = (data.rows || []).map((r) => headers.map((h) => r[h]));

        // Top3-Berechnung für Race-Spalten M..X
        const top3PerRaceColumn = computeTop3ByColumn(rowsAsArrays, LETTER_M, LETTER_X);

        // Anzahl bereits gefahrener Läufe (mind. eine Zahl in der Spalte)
        let completedRuns = 0;
        for (let c = LETTER_M; c <= LETTER_X; c++) {
            const anyNumberInCol = rowsAsArrays.some(row => {
                const v = row[c - 1];
                return v !== null && v !== "" && !Number.isNaN(Number(v));
            });
            if (anyNumberInCol) completedRuns++;
        }

        return {
            visibleHeaders,
            idxTeamLogoUrl,
            rowsAsArrays,
            top3PerRaceColumn,
            completedRuns,
        };
    }, [data]);

    if (loading) return <div className="card"><p>Lade Standings...</p></div>;
    if (noData) {
        return (
            <div className="card">
                <h2>Standings – TEAM</h2>
                <p className="muted">
                    Aktuell sind noch keine Rennergebnisse im System.
                    Sobald das erste Rennen gefahren ist, erscheinen hier die TEAM-Standings der GT3 Xperience.
                </p>
            </div>
        );
    }
    if (error)   return <div className="card"><p style={{ color:"#f55" }}>Fehler: {error}</p></div>;

    return (
        <>
            <div className="card">
                <h2>Standings – Teams</h2>
                <p className="muted">
                    Teamwertung nach Abschluss des {completedRuns}. Laufes präsentiert von BavarianSimTec <br/>
                    Die Teamwertung wird aus der Gesamtwertung des Laufes gebildet mit den Top 15 Fahrern. Pro Team können pro Lauf jedoch maximal zwei Fahrer gewertet werden.
                </p>
            </div>

            <div className="fancyTable">
                <table className="tbl">
                    <thead>
                    <tr>
                        {visibleHeaders.map((h, i) => {
                            const colNumber = i + 1;
                            if (colNumber === 5) return null; // Spalte E skippen

                            // Kopfzeilen-Labels anpassen
                            // A Pos, B Δ, C (Icon leer, zeigt Teamlogo), D Team, E Fahrer (breit), F Pts, G Diff, H Inc, I R, J W, K P, L DNF
                            const label =
                                colNumber === 1 ? "Pos" :
                                    colNumber === 2 ? "Δ"   :
                                        colNumber === 6 ? "Pts" :
                                            colNumber === 7 ? "Diff":
                                                colNumber === 8 ? "Inc" :
                                                    colNumber === 9 ? "R"   :
                                                        colNumber === 10 ? "W"  :
                                                            colNumber === 11 ? "P"  :
                                                                colNumber === 12 ? "DNF":
                                                                    h;

                            // Rennspalten: M..X (13..24)
                            const isRace    = colNumber >= LETTER_M && colNumber <= LETTER_X;
                            const raceLeft  = colNumber === LETTER_M;
                            const raceRight = colNumber === LETTER_X;
                            const evenStripe = isRace && ((colNumber - LETTER_M) % 2 === 0);

                            // Linksbündig A (Pos), D (Team), E (Fahrer)
                            const leftAligned = (colNumber === 1 || colNumber === 4 || colNumber === 5);

                            // C = Teamlogo-Iconspalte
                            const thClass = [
                                isRace ? "race-col" : "",
                                raceLeft ? "race-left" : "",
                                raceRight ? "race-right" : "",
                                evenStripe ? "race-alt" : "",
                                (colNumber === 3) ? "icon" : "",
                                leftAligned ? "align-left" : "",
                            ].filter(Boolean).join(" ");

                            return <th key={i} className={thClass}>{label}</th>;
                        })}
                    </tr>
                    </thead>

                    <tbody>
                    {rowsAsArrays.map((arr, ri) => (
                        <tr key={ri}>
                            {visibleHeaders.map((_, i) => {
                                const colNumber = i + 1;
                                if (colNumber === 5) return null; // Spalte E skippen
                                const val = arr[i];

                                // C: Teamlogo aus AB
                                if (colNumber === 3) {
                                    const src = arr[idxTeamLogoUrl] || "";
                                    return (
                                        <td key={i} className="icon">
                                            {src ? <img src={src} alt="Team" className="teamlogo lg" loading="lazy" /> : null}
                                        </td>
                                    );
                                }

                                // Rennspalten M..X mit Top-3-Highlight
                                const isRace    = colNumber >= LETTER_M && colNumber <= LETTER_X;
                                const raceLeft  = colNumber === LETTER_M;
                                const raceRight = colNumber === LETTER_X;
                                const evenStripe = isRace && ((colNumber - LETTER_M) % 2 === 0);

                                let tierClass = "";
                                if (isRace && val !== "" && val !== null && val !== undefined) {
                                    const numVal = Number(val);
                                    const t3 = top3PerRaceColumn.get(colNumber);
                                    if (t3 && !Number.isNaN(numVal)) {
                                        if (numVal === t3.gold)        tierClass = "race-gold";
                                        else if (numVal === t3.silver) tierClass = "race-silver";
                                        else if (numVal === t3.bronze) tierClass = "race-bronze";
                                    }
                                }

                                // Linksbündig A (Pos), D (Team), E (Fahrer)
                                const leftAligned = (colNumber === 1 || colNumber === 4 || colNumber === 5);

                                // Delta (B) einfärben
                                let deltaClass = "";
                                if (colNumber === 2) {
                                    const n = Number(val);
                                    if (!isNaN(n)) {
                                        if (n > 0) deltaClass = "delta-up";
                                        else if (n < 0) deltaClass = "delta-down";
                                    }
                                }

                                // Kompakte Metriken: F..L (Points..DNFs = 6..12)
                                const compactMetrics = (colNumber >= 6 && colNumber <= 12) ? "compact-metrics" : "";

                                const numeric =
                                    isNumeric(val) ||
                                    (colNumber >= 6 && colNumber <= 12) || // Points..DNFs
                                    isRace ||
                                    (colNumber >= LETTER_Y && colNumber <= LETTER_AA); // Drops

                                const tdClass = [
                                    numeric ? "num" : "",
                                    isRace ? "race-col" : "",
                                    raceLeft ? "race-left" : "",
                                    raceRight ? "race-right" : "",
                                    evenStripe ? "race-alt" : "",
                                    tierClass,
                                    leftAligned ? "align-left" : "",
                                    (colNumber === 6) ? "points-highlight" : "", // Points (F)
                                    (colNumber === 1) ? "pos-highlight" : "",    // Pos (A)
                                    deltaClass,
                                    compactMetrics,
                                ].filter(Boolean).join(" ");

                                // Fahrer-Spalte (E) kann sehr breit sein → Titel/Tooltip optional
                                const content = (colNumber === 5 && typeof val === "string" && val.length > 0)
                                    ? <span title={val}>{val}</span>
                                    : val;

                                return <td key={i} className={tdClass}>{content}</td>;
                            })}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}
